import { useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../api/invoices';
import { personsApi } from '../../api/persons';
import { taxRatesApi } from '../../api/taxRates';
import { accountsApi } from '../../api/accounts';
import { catalogApi } from '../../api/catalog';
import { inventoryMirrorApi } from '../../api/inventory';
import { cround, formatMoney } from '../../utils/formatMoney';
import LoadingSpinner from '../../components/LoadingSpinner';
import type { UpdateInvoiceMadeRequest, UpdateInvoiceReceivedRequest, UpdateInvoiceLineRequest } from '../../types/invoice';
import { useAppBack, useBeforeUnload } from '../../hooks/useAppBack';
import { useIsMirror } from '../../hooks/useIsMirror';

interface LineForm {
  name: string;
  unit: string;
  account: string;
  vatAccount: string;
  unitPrice: string;
  quantity: string;
  vatRateId: string;
  discount: string;
  /** B5 stock link (round-trips through the line DTO so edits never wipe it). */
  goodsId: string;
  warehouseId: string;
}

const emptyLine = (): LineForm => ({
  name: '',
  unit: 'vnt.',
  account: '',
  vatAccount: '',
  unitPrice: '0',
  quantity: '1',
  vatRateId: '',
  discount: '0',
  goodsId: '',
  warehouseId: '',
});

export default function InvoiceEdit() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const direction = searchParams.get('direction') ?? 'Made';
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — a
  // deep-linked edit route gets the read-only view instead of the form;
  // the API 403s writes anyway (defense in depth).
  const isMirror = useIsMirror();

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice-detail', id, direction],
    queryFn: () => invoicesApi.getById(Number(id), direction),
    enabled: !!id,
  });

  const [date, setDate] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [content, setContent] = useState('');
  const [personId, setPersonId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [currencyRate, setCurrencyRate] = useState('1');
  const [customInfo, setCustomInfo] = useState('');
  const [vatExemptions, setVatExemptions] = useState('');
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  // Finding 35: unsaved-invoice guard — USER edits only (the form's onChange
  // delegation fires on real input events, never on the programmatic
  // populate useMemo above).
  const [dirty, setDirty] = useState(false);
  useAppBack({ enabled: true, blocking: dirty });
  useBeforeUnload(dirty);

  // Populate form when invoice data loads
  useMemo(() => {
    if (invoice && !initialized) {
      setDate(invoice.date?.split('T')[0] ?? '');
      setDocumentNumber(invoice.documentNumber);
      setContent(invoice.content);
      setPersonId(invoice.personId?.toString() ?? '');
      setCurrencyCode(invoice.currencyCode);
      setCurrencyRate(invoice.currencyRate.toString());
      setCustomInfo(invoice.customInfo ?? '');
      setVatExemptions(invoice.vatExemptions ?? '');
      if (invoice.lines.length > 0) {
        setLines(
          invoice.lines.map((l) => ({
            name: l.name,
            unit: l.unit || 'vnt.',
            account: l.account.toString(),
            vatAccount: l.vatAccount?.toString() ?? '',
            unitPrice: l.unitPrice.toString(),
            quantity: l.quantity.toString(),
            vatRateId: '',
            discount: '0',
            goodsId: l.goodsId != null ? String(l.goodsId) : '',
            warehouseId: l.warehouseId != null ? String(l.warehouseId) : '',
          }))
        );
      }
      setInitialized(true);
    }
  }, [invoice, initialized]);

  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates-all'],
    queryFn: taxRatesApi.list,
  });

  const { data: goodsCatalog } = useQuery({
    queryKey: ['catalog-goods-all'],
    queryFn: () => catalogApi.list({ type: 'goods' }),
  });
  const { data: warehouses } = useQuery({
    queryKey: ['inventory-warehouses'],
    queryFn: inventoryMirrorApi.warehouses,
  });
  const goodsOptions = (goodsCatalog ?? []).filter((g) => !g.isObsolete);

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; payload: UpdateInvoiceMadeRequest | UpdateInvoiceReceivedRequest }) =>
      direction === 'Made'
        ? invoicesApi.updateMade(data.id, data.payload as UpdateInvoiceMadeRequest)
        : invoicesApi.updateReceived(data.id, data.payload as UpdateInvoiceReceivedRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', id, direction] });
      navigate(`/invoices/${id}?direction=${direction}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateLine = (index: number, field: keyof LineForm, value: string) => {
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current) {
        next[index] = { ...current, [field]: value } as LineForm;
      }
      return next;
    });
  };

  // B5 stock link pickers (same semantics as the create form).
  const selectGoods = (index: number, goodsId: string) => {
    const goods = goodsId ? goodsOptions.find((g) => g.id === Number(goodsId)) : undefined;
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (!current) return prev;
      if (!goods) {
        next[index] = { ...current, goodsId: '', warehouseId: '' };
        return next;
      }
      next[index] = {
        ...current,
        goodsId,
        name: current.name || goods.name,
        unit: goods.unit || current.unit,
      };
      return next;
    });
  };

  const selectLineWarehouse = (index: number, warehouseId: string) => {
    const warehouse = warehouseId ? warehouses?.find((w) => w.id === Number(warehouseId)) : undefined;
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (!current) return prev;
      next[index] = {
        ...current,
        warehouseId,
        account: direction === 'Received' && warehouse ? String(warehouse.warehouseAccount) : current.account,
      };
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const getTaxRate = (id: string) => {
    if (!id) return 0;
    return taxRates?.find((r) => r.id === Number(id))?.rate ?? 0;
  };

  const buildLineRequest = (line: LineForm): { form: LineForm; request: UpdateInvoiceLineRequest } => {
    const unitPrice = parseFloat(line.unitPrice) || 0;
    const quantity = parseFloat(line.quantity) || 0;
    const vatRate = getTaxRate(line.vatRateId);
    const discount = direction === 'Made' ? parseFloat(line.discount) || 0 : 0;
    const gross = cround(unitPrice * quantity);
    // B6: line arithmetic in the ORIGINAL currency; base = original × rate.
    const rate = currencyCode === 'EUR' ? 1 : parseFloat(currencyRate) || 1;
    const sumOriginal = cround(gross - Math.min(discount, gross));
    const sumVatOriginal = cround(sumOriginal * vatRate);
    const sum = rate === 1 ? sumOriginal : cround(sumOriginal * rate);
    const sumVat = rate === 1 ? sumVatOriginal : cround(sumVatOriginal * rate);

    return {
      form: line,
      request: {
        name: line.name,
        unit: line.unit || 'vnt.',
        account: parseInt(line.account) || 0,
        vatAccount: line.vatAccount ? parseInt(line.vatAccount) : null,
        unitPrice,
        quantity,
        vatRate,
        sum,
        sumVat,
        discount,
        goodsId: line.goodsId ? parseInt(line.goodsId) : null,
        warehouseId: line.warehouseId ? parseInt(line.warehouseId) : null,
        sumOriginal,
        sumVatOriginal,
      },
    };
  };

  const computedLines = lines.map(buildLineRequest);
  const totalSum = computedLines.reduce((acc, l) => acc + l.request.sum, 0);
  const totalVat = computedLines.reduce((acc, l) => acc + l.request.sumVat, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!documentNumber.trim()) { setError('Dokumento numeris yra būtinas'); return; }
    if (!content.trim()) { setError('Turinys yra būtinas'); return; }
    if (!personId) { setError(`${direction === 'Made' ? 'Klientas' : 'Tiekėjas'} yra būtinas`); return; }

    const lineRequests = computedLines.map((l) => l.request);
    const invalidLine = lineRequests.find((l) => !l.account || !l.name);
    if (invalidLine) { setError('Kiekviena eilutė turi turėti pavadinimą ir sąskaitą'); return; }

    const payload = {
      date,
      documentNumber: documentNumber.trim(),
      content: content.trim(),
      personId: parseInt(personId),
      currencyCode,
      currencyRate: parseFloat(currencyRate) || 1,
      lines: lineRequests,
      // Send always on the Made side ("" clears); absent on the Received side
      // so the server leaves the stored value untouched.
      ...(direction === 'Made'
        ? { customInfo: customInfo.trim(), vatExemptions: vatExemptions.trim() }
        : {}),
    };

    updateMutation.mutate({ id: Number(id), payload });
  };

  if (isLoading) return <LoadingSpinner />;
  if (!invoice) return <div className="text-center py-8 text-gray-500">Sąskaita faktūra nerasta</div>;

  // Mirror workspaces: the edit form stays fillable; only saving is disabled
  // (ADR-004 - the book is written by the Apskaita5 app).

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          Redaguoti sąskaitą faktūrą Nr. {invoice.documentNumber}
        </h1>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          onClick={() => navigate(-1)}
        >
          Atšaukti
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}

      {isMirror && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
          Ši darbo sritis tik skaitymui. Pakeitimus įrašykite Apskaita5 programoje, o čia juos pamatysite automatiškai.
        </div>
      )}

      <form onChange={() => setDirty(true)} onSubmit={handleSubmit} className="space-y-4">
        {/* Header fields */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Kryptis</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50"
                value={direction}
                disabled
              >
                <option value="Made">Pardavimas (išrašyta)</option>
                <option value="Received">Pirkimas (gauta)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data</label>
              <input
                type="date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dokumento nr.</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{direction === 'Made' ? 'Klientas' : 'Tiekėjas'}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
              >
                <option value="">-- Pasirinkite --</option>
                {persons?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiuta</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyCode}
                onChange={(e) => {
                  setCurrencyCode(e.target.value);
                  if (e.target.value === 'EUR') setCurrencyRate('1');
                }}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="DKK">DKK</option>
                <option value="SEK">SEK</option>
                <option value="NOK">NOK</option>
                <option value="PLN">PLN</option>
                <option value="CZK">CZK</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiutos kursas (į EUR)</label>
              <input
                type="number"
                step="0.000001"
                min="0.000001"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:bg-gray-100"
                value={currencyRate}
                onChange={(e) => setCurrencyRate(e.target.value)}
                disabled={currencyCode === 'EUR'}
                title={currencyCode === 'EUR' ? 'EUR sąskaitos kursas visada 1' : '1 svetimos valiutos vienetas EUR'}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Turinys</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Document text (sales invoices only — printed on the PDF) */}
        {direction === 'Made' && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Papildoma informacija (spausdinama sąskaitoje)</label>
              <textarea
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                rows={2}
                value={customInfo}
                onChange={(e) => setCustomInfo(e.target.value)}
                placeholder="pvz. Apmokėti iki 2026-10-25"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">PVM išimčių pagrindimas (jei taikoma)</label>
              <textarea
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                rows={2}
                value={vatExemptions}
                onChange={(e) => setVatExemptions(e.target.value)}
                placeholder="pvz. PVM neapmokama pagal LR PVM įstatymo 95 str."
              />
            </div>
          </div>
        )}

        {/* Line items */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-sm">Eilutės</h2>
            <button
              type="button"
              className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
              onClick={addLine}
            >
              + Pridėti eilutę
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left min-w-32">Pavadinimas</th>
                  <th className="text-left w-40">Prekė / sandėlis</th>
                  <th className="text-left w-20">Vnt.</th>
                  <th className="text-right w-20">Sąskaita</th>
                  <th className="text-right w-20">Kaina</th>
                  <th className="text-right w-16">Kiekis</th>
                  {direction === 'Made' && <th className="text-right w-20">Nuolaida</th>}
                  <th className="text-left w-28">PVM tarifas</th>
                  <th className="text-right w-20">Suma</th>
                  <th className="text-right w-20">PVM</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const built = buildLineRequest(line);
                  return (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.name}
                          onChange={(e) => updateLine(i, 'name', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.goodsId}
                          onChange={(e) => selectGoods(i, e.target.value)}
                        >
                          <option value="">—</option>
                          {goodsOptions.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                        {line.goodsId && (
                          <select
                            className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded mt-1"
                            value={line.warehouseId}
                            onChange={(e) => selectLineWarehouse(i, e.target.value)}
                          >
                            <option value="">Sandėlis: numatytasis</option>
                            {(warehouses ?? []).filter((w) => !w.isObsolete).map((w) => (
                              <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.unit}
                          onChange={(e) => updateLine(i, 'unit', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.account}
                          onChange={(e) => updateLine(i, 'account', e.target.value)}
                          required
                        >
                          <option value="">--</option>
                          {accounts?.map((a) => (
                            <option key={a.accountNumber} value={a.accountNumber}>
                              {a.accountNumber} {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.unitPrice}
                          onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        />
                      </td>
                      {direction === 'Made' && (
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                            value={line.discount}
                            onChange={(e) => updateLine(i, 'discount', e.target.value)}
                          />
                        </td>
                      )}
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.vatRateId}
                          onChange={(e) => updateLine(i, 'vatRateId', e.target.value)}
                        >
                          <option value="">Be PVM</option>
                          {taxRates?.filter((r) => !r.isObsolete).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} ({(r.rate * 100).toFixed(0)}%)
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num">{formatMoney(built.request.sum, false)}</td>
                      <td className="num">{formatMoney(built.request.sumVat, false)}</td>
                      <td>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeLine(i)}
                          disabled={lines.length <= 1}
                        >
                          x
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={direction === 'Made' ? 8 : 7} className="text-right font-semibold">Viso</td>
                  <td className="num font-semibold">{formatMoney(totalSum)}</td>
                  <td className="num font-semibold">{formatMoney(totalVat)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={direction === 'Made' ? 8 : 7} className="text-right font-bold">Viso su PVM</td>
                  <td colSpan={2} className="num font-bold text-primary-700">{formatMoney(totalSum + totalVat)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={() => navigate(-1)}
          >
            Atšaukti
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending || isMirror}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? 'Išsaugoma…' : 'Išsaugoti pakeitimus'}
          </button>
        </div>
      </form>
    </div>
  );
}
